<!-- Base.Button.vue (client-lms) -->
<template>
  <button :type="type" :class="buttonClasses" :disabled="disabled">
    <i v-if="icon && iconPosition === 'before'" :class="[icon]"></i>
    <slot></slot>
    <i v-if="icon && iconPosition === 'after'" :class="[icon]"></i>
  </button>
</template>

<script setup lang="ts">
import { computed, type PropType } from "vue";

const props = defineProps({
  type: {
    type: String as PropType<"button" | "reset" | "submit">,
    default: "button",
  },
  color: {
    type: String,
    default: "gray", // 'gray', 'black', 'white', 'text'
  },
  size: {
    type: String as PropType<"36" | "48">,
    default: "48", // '48', '36'
  },
  shape: {
    type: String,
    default: "",
  },
  txt: {
    type: String,
    default: "text",
  },
  iconPosition: {
    type: String,
    default: "before",
  },
  icon: {
    type: String,
    default: "",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const buttonClasses = computed(() => {
  return [
    "lms_btn",
    `lms_btn_${props.color}`,
    `lms_btn_${props.size}`,
    { lms_btn_icon: props.icon },
    { icon_only: props.txt === "none" },
    { lms_btn_circle: props.shape === "circle" },
    { lms_btn_round: props.shape === "round" },
  ];
});
</script>

<style scoped>
/* Contained Button Base */
.lms_btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  border-radius: 4px;
  font-family: "Pretendard", sans-serif;
  font-weight: 600;
  cursor: pointer;
  transition:
    background-color 0.2s,
    opacity 0.2s;
  white-space: nowrap;
  line-height: 1;
}

/* Size: 48 */
.lms_btn_48 {
  height: 48px;
  padding: 0 24px;
  font-size: 16px;
}

/* Size: 36 */
.lms_btn_36 {
  height: 36px;
  padding: 0 16px;
  font-size: 14px;
}

/* Color: Gray (Contained) */
.lms_btn_gray {
  background-color: var(--lms-grey-200, #f1f2f2);
  color: var(--lms-on-surface-high-var, rgba(0, 0, 0, 0.7));
}
.lms_btn_gray:hover {
  background-color: var(--lms-grey-200, #f1f2f2);
}
.lms_btn_gray:active {
  background-color: var(--lms-grey-300, #eaebeb);
}
.lms_btn_gray:disabled {
  background-color: var(--lms-grey-400);
  color: #fff;
  cursor: not-allowed;
}

/* Color: Black (Contained) */
.lms_btn_black {
  background-color: var(--lms-on-surface-high, rgba(0, 0, 0, 0.8));
  color: var(--lms-on-primary-high, #ffffff)  ;
}
.lms_btn_black:hover {
  background-color: #3a3b3b;
}
.lms_btn_black:active {
  background-color: #1a1b1b;
}
.lms_btn_black:disabled {
  background-color: rgba(0, 0, 0, 0.35);
  color: #fff;
  cursor: not-allowed;
}

/* Color: White (Contained) */
.lms_btn_white {
  background-color: var(--lms-surface, #ffffff);
  color: var(--lms-on-surface-high-var, rgba(0, 0, 0, 0.7));
  border: 1px solid var(--lms-outline-gray-main, #e0e0e0);
}
.lms_btn_white:hover {
  background-color: var(--lms-grey-100, #f6f7f7);
}
.lms_btn_white:active {
  background-color: var(--lms-grey-200, #f1f2f2);
}
.lms_btn_white:disabled {
  background-color: #fff;
  color: var(--lms-on-surface-low-var, rgba(0, 0, 0, 0.18));
  border-color: var(--lms-outline-gray-main, #E0E0E0);
  cursor: not-allowed;
}

/* Color: Text (Text Button) */
.lms_btn_text {
  background-color: transparent;
  color: var(--lms-on-surface-high-var, rgba(0, 0, 0, 0.7));
  padding-left: 8px;
  padding-right: 8px;
}
.lms_btn_text:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
.lms_btn_text:active {
  background-color: rgba(0, 0, 0, 0.08);
}
.lms_btn_text:disabled {
  color: var(--lms-on-surface-low-var, rgba(0, 0, 0, 0.18));
  cursor: not-allowed;
}

/* Color: primary (alias for backward compat) */
.lms_btn_primary {
  background-color: var(--lms-primary-400, #5c74f6);
  color: var(--lms-on-primary-high, #ffffff);
}
.lms_btn_primary:active {
  background-color: #3b53d5;
}
.lms_btn_primary:disabled {
  background-color: var(--lms-grey-400, #c8c9c9);
  color: var(--lms-on-primary-high, #ffffff);
  cursor: not-allowed;
}

/* Color: default (alias for gray) */
.lms_btn_default {
  background-color: var(--lms-grey-100, #f6f7f7);
  color: var(--lms-on-surface-high-var, rgba(0, 0, 0, 0.7));
}
.lms_btn_default:hover {
  background-color: var(--lms-grey-200, #f1f2f2);
}
.lms_btn_default:active {
  background-color: var(--lms-grey-300, #eaebeb);
}
.lms_btn_default:disabled {
  background-color: var(--lms-grey-100, #f6f7f7);
  color: var(--lms-on-surface-low-var, rgba(0, 0, 0, 0.18));
  cursor: not-allowed;
}

/* Color: none (transparent) */
.lms_btn_none {
  background-color: transparent;
  color: var(--lms-on-surface-medium-var, rgba(0, 0, 0, 0.5));
}
.lms_btn_none:hover {
  color: var(--lms-on-surface-high-var, rgba(0, 0, 0, 0.7));
}

/* Icon */
.lms_btn_icon i {
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
}

.icon_only {
  padding: 0;
  width: 36px;
  height: 36px;
  font-size: 0;
}
.lms_btn_48.icon_only {
  width: 48px;
  height: 48px;
}

/* Shape */
.lms_btn_circle {
  border-radius: 50%;
}
.lms_btn_round {
  border-radius: 100px;
}
</style>
